import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

// ── HTTP error message overrides ───────────────────────────────────────────────

export type HttpErrorMessages = {
  /** Override for 403 Forbidden responses. */
  forbidden?: string
  /** Override for 404 Not Found responses. */
  notFound?: string
  /** Override for 429 Too Many Requests responses. */
  tooManyRequests?: string
  /** Override for 5xx Server Error responses. */
  serverError?: string
  /** Fallback for all other error statuses. */
  default?: string
}

/** Default user-facing messages for common HTTP error statuses. */
export const DEFAULT_HTTP_ERROR_MESSAGES = {
  forbidden: "You don't have permission to perform this action.",
  notFound: 'The requested resource was not found.',
  tooManyRequests: 'Too many requests. Please wait a moment and try again.',
  serverError: 'Server error occurred. Please try again later.',
  default: 'Request failed. Please try again.',
} as const satisfies Required<HttpErrorMessages>

/**
 * Merges caller-supplied overrides with {@link DEFAULT_HTTP_ERROR_MESSAGES},
 * returning a complete set of error strings.
 *
 * @example
 * const messages = resolveHttpErrorMessages({
 *   forbidden: "You don't have permission to export results.",
 * });
 * // messages.forbidden  → "You don't have permission to export results."
 * // messages.notFound   → "The requested resource was not found."  (default)
 */
export function resolveHttpErrorMessages(
  overrides: HttpErrorMessages = {},
): Required<HttpErrorMessages> {
  return { ...DEFAULT_HTTP_ERROR_MESSAGES, ...overrides }
}

/**
 * Resolves the user-facing message for a given HTTP status code, applying
 * caller-supplied overrides where provided.
 *
 * @example
 * const message = resolveHttpErrorMessage(429, { tooManyRequests: "Export limit reached." });
 */
export function resolveHttpErrorMessage(
  status: FetchBaseQueryError['status'] | number | undefined,
  overrides: HttpErrorMessages = {},
): string {
  const messages = resolveHttpErrorMessages(overrides)

  switch (status) {
    case 403:
      return messages.forbidden
    case 404:
      return messages.notFound
    case 429:
      return messages.tooManyRequests
    default:
      return typeof status === 'number' && status >= 500
        ? messages.serverError
        : messages.default
  }
}
